import { useState, useEffect, useRef } from 'react';
import apiService from '../services/api';

// Realm directories routinely hold more entries than any single page — a picker
// that filters a client-side snapshot silently hides everyone past that page, so
// the admin sees "no such user" for someone who plainly exists. Typing therefore
// has to reach Keycloak, exactly the way the Users and Groups pages already do.
export const DIRECTORY_SEARCH_PAGE_SIZE = 50;

const DIRECTORIES = {
  users: { fetch: (params) => apiService.getAllUsers(params), collection: 'users' },
  groups: { fetch: (params) => apiService.getAllGroups(params), collection: 'groups' },
};

/**
 * Debounced server-side directory lookup for pickers.
 *
 * Returns matches only while `query` is non-empty; callers fall back to whatever
 * list they already hold for the unfiltered case, so the default view — and any
 * "select all" built on it — behaves exactly as before.
 *
 * @param {'users'|'groups'} kind  directory to search
 * @param {string} query           raw text from the picker's search box
 * @param {{enabled?: boolean, delay?: number}} [options]
 * @returns {{results: Array, total: number, loading: boolean, truncated: boolean}}
 */
export function useDirectorySearch(kind, query, { enabled = true, delay = 300 } = {}) {
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);

  const trimmed = (query || '').trim();

  useEffect(() => {
    if (!enabled || !trimmed) {
      // Invalidate anything still in flight so a late response cannot repopulate
      // the list after the admin cleared the box.
      seqRef.current += 1;
      setResults([]);
      setTotal(0);
      setLoading(false);
      return undefined;
    }

    const directory = DIRECTORIES[kind];
    if (!directory) {
      return undefined;
    }

    setLoading(true);
    const timer = setTimeout(async () => {
      const seq = ++seqRef.current;
      try {
        const response = await directory.fetch({
          page: 1,
          pageSize: DIRECTORY_SEARCH_PAGE_SIZE,
          search: trimmed,
        });
        // A newer keystroke already won; its response is the authoritative one.
        if (seq !== seqRef.current) return;
        const data = response?.data || {};
        const items = data[directory.collection] || [];
        setResults(items);
        setTotal(typeof data.total === 'number' ? data.total : items.length);
      } catch (err) {
        if (seq !== seqRef.current) return;
        console.error(`Failed to search ${kind}:`, err);
        setResults([]);
        setTotal(0);
      } finally {
        if (seq === seqRef.current) setLoading(false);
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [kind, trimmed, enabled, delay]);

  return {
    results,
    total,
    loading,
    // More matches exist than this page carries — worth telling the admin so an
    // incomplete list is not mistaken for the whole answer.
    truncated: total > results.length,
  };
}

export default useDirectorySearch;
